/*
 *  -----------------------------------------------------------------------
 *    <copyright file="step2-heat-map.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */
import FormControl from '@mui/material/FormControl';
import React, { useState, useImperativeHandle, forwardRef, useEffect } from 'react';
import { useBoolean, useTranslation } from 'hooks';
import 'views/components/central-monitoring/panels/add-panel/styles.scss';
import { checkRequiredFields, validateFields } from 'utils/validation';
import DataSourceSelector from 'views/components/central-monitoring/data-source-selector';
import { FieldSelector, Formats } from 'views/components/central-monitoring/field-selector';
import PanelConstants from 'views/components/central-monitoring/panels/add-panel/panel-constants';
import SelectWithCategories from 'views/components/core/select-with-categories';
import Constants from '@utils/constants';
import { PanelOrderBy } from '@types/graphql-global-types';

const { displayBy, heatmapOrders } = PanelConstants;

const Step2HeatMap = forwardRef(
    (
        {
            show,
            panelState,
            setPanelState,
            setPage,
            page,
            updateState,
            updateErrorState,
            canSave,
            scheduleOptions,
        }: any,
        ref,
    ) => {
        const { t } = useTranslation();
        const initialState = {
            dataSource: panelState.dataSource.dataSource,
            xAxisDataField: panelState.dataSource.xAxisDataField,
            yAxisDataField: panelState.dataSource.yAxisDataField,
            sortField: panelState.dataSource.sortField,
            countField: panelState.dataSource.countField,
            rateByField: panelState.dataSource.rateByField,
            rateByType: panelState.dataSource.rateByType,
            timePeriod: panelState.dataSource.timePeriod,
            dateField: panelState.dataSource.dateField,
            flagField: panelState.dataSource.flagField,
            displayBy: panelState.graph.displayBy,
            orderBy: panelState.graph.orderBy ?? PanelOrderBy.LABEL_ASCENDING,
            scheduleId: panelState.scheduleId,
        };
        const errorsState = {
            dataSource: '',
            xAxisDataField: '',
            yAxisDataField: '',
            sortField: '',
            countField: '',
            rateByField: '',
            rateByType: '',
            timePeriod: '',
            dateField: '',
            flagField: '',
            displayBy: '',
            orderBy: '',
            scheduleId: '',
        } as any;

        const [state, setState] = useState({ ...initialState });
        const [errors, setErrors] = useState({ ...errorsState });
        const scheduleInactive = useBoolean(false);

        const validate = (value?: string, field?: string, length?: number, required?: boolean) => {
            let error = validateFields(value, field, length, required);
            if (field === 'orderBy') {
                error = { ...error, scheduleId: '' };
            }
            if (
                field === 'scheduleId' &&
                state.orderBy === PanelOrderBy.LABEL_VISIT_SEQUENCE_NUMBER &&
                value === Constants.ObjectId.Empty
            ) {
                error = { scheduleId: t('ra:requiredFieldError') };
            }
            setErrors({
                ...errors,
                ...error,
            });
        };

        const onChange = (fieldName: string, length?: number, required?: boolean, key?: string, value?: string) => {
            if (fieldName === 'dataSource') {
                setState({ ...state, dataSource: value, xAxisDataField: '' });
                updateState({ target: { name: 'dataSource.dataSource', value } });
                updateState({ target: { name: 'dataSource.xAxisDataField', value: '' } });
            } else {
                setState({ ...state, [fieldName]: value });
                updateState({ target: { name: key || fieldName, value } });
                if (fieldName === 'orderBy') {
                    updateState({ target: { name: 'scheduleId', value: '' } });
                }
            }

            validate(value, fieldName, length, required);
        };

        const validationOnUpdate = () => {
            const requiredFields = ['dataSource', 'xAxisDataField', 'displayBy'];
            const isDefaultScheduleId =
                state.orderBy === PanelOrderBy.LABEL_VISIT_SEQUENCE_NUMBER &&
                state.scheduleId === Constants.ObjectId.Empty;

            if (state.orderBy === PanelOrderBy.LABEL_VISIT_SEQUENCE_NUMBER) requiredFields.push('scheduleId');

            const updateErrors = (errs: { [key: string]: string }[]) => {
                setErrors({
                    ...errs,
                    ...(isDefaultScheduleId ? { scheduleId: t('ra:requiredFieldError') } : {}),
                });
            };

            return (
                checkRequiredFields({ fields: requiredFields, state, setErrors: updateErrors, errors }) &&
                !isDefaultScheduleId
            );
        };

        const onNext = () => {
            if (validationOnUpdate()) {
                setPanelState({
                    ...panelState,
                    dataSource: {
                        ...panelState.dataSource,
                        dataSource: state.dataSource,
                        xAxisDataField: state.xAxisDataField,
                        yAxisDataField: state.yAxisDataField,
                        sortField: state.sortField,
                        countField: state.countField,
                        rateByField: state.rateByField,
                        timePeriod: state.timePeriod,
                        dateField: state.dateField,
                        flagField: state.flagField,
                    },
                });
                setPage({ ...page, number: 3, available: 3 });
            } else if (page.number !== 2) {
                setPage({ ...page, number: 2, available: page.available });
            }
        };

        const cleanStep = () => {
            setState({ ...initialState });
            setErrors({ ...errorsState });
        };

        useEffect(() => {
            const scheduleOption = scheduleOptions.find((x: any) => x.id === state.scheduleId);
            scheduleInactive.setValue(scheduleOption?.id === state.scheduleId && scheduleOption?.active === false);
        }, [state.scheduleId, scheduleOptions]);

        useEffect(() => {
            updateErrorState(errors, 2);
        }, [errors]);

        useImperativeHandle(ref, () => ({
            nextButton() {
                onNext();
            },
            cleanStep() {
                cleanStep();
            },
            validationOnUpdate() {
                return validationOnUpdate();
            },
        }));

        return (
            <div style={!show ? { display: 'none' } : {}}>
                <div className="type-edc-wrapper mb-30 mt">
                    <FormControl variant="outlined" className="form-control-type not-full mr">
                        <DataSourceSelector
                            customClassName="edit-atts-category"
                            fieldSelect="dataSource"
                            onChange={(value: string) => {
                                onChange('dataSource', null, false, 'dataSource.dataSource', value);
                            }}
                            label={t('admin:rowData')}
                            labelField="dataSource"
                            value={state.dataSource}
                            error={errors.dataSource}
                            showDefault={false}
                            searchable
                            tag="PANEL"
                            disabled={!canSave}
                        />
                    </FormControl>
                    <FormControl variant="outlined" className="form-control-type not-full">
                        <FieldSelector
                            label={t('admin:xAxisCategory')}
                            dataSource={state.dataSource}
                            formats={[Formats.Text, Formats.Int, Formats.Bool]}
                            value={state.xAxisDataField}
                            onChange={(value: string) => {
                                onChange('xAxisDataField', null, false, 'dataSource.xAxisDataField', value);
                            }}
                            labelField="xAxisDataField"
                            fieldSelect="xAxisDataField"
                            customId="x-axis-category-id"
                            error={errors.xAxisDataField}
                            searchable
                            disabled={!canSave}
                        />
                    </FormControl>
                </div>
                <div className="type-edc-wrapper">
                    <FormControl variant="outlined" className="form-control-type not-full mr">
                        <SelectWithCategories
                            label={t('admin:displayBy')}
                            dataArray={displayBy}
                            fieldSelect="displayBy"
                            onChange={(fieldName?: string) => (event: any) => {
                                onChange(fieldName, null, false, 'graph.displayBy', event.target?.value);
                            }}
                            currentState={state.displayBy}
                            labelField="displayBy"
                            showWarning={false}
                            filterable={false}
                            customLabelId="display-units-label-id"
                            customId="display-units-id"
                            customClassName="edit-atts-column-category"
                            error={errors.displayBy}
                        />
                    </FormControl>
                    <FormControl variant="outlined" className="form-control-type not-full">
                        <SelectWithCategories
                            label={t('admin:xAxisLabelOrder')}
                            dataArray={heatmapOrders}
                            onChange={(fieldName?: string) => (event: any) => {
                                onChange(fieldName, null, false, 'graph.orderBy', event.target?.value);
                            }}
                            fieldSelect="orderBy"
                            currentState={state.orderBy}
                            labelField="orderBy"
                            showWarning={false}
                            filterable={false}
                            customLabelId="orderby-column-label-id"
                            customId="orderby-id"
                            customClassName="edit-atts-column-category"
                            error={errors.orderBy}
                        />
                    </FormControl>
                </div>
                {state.orderBy === PanelOrderBy.LABEL_VISIT_SEQUENCE_NUMBER && (
                    <div className="type-edc-wrapper">
                        <FormControl variant="outlined" className="form-control-type not-full mr">
                            <SelectWithCategories
                                label={t('admin:scheduleNameR')}
                                dataArray={scheduleOptions}
                                onChange={() => (event: any) => {
                                    onChange('scheduleId', null, true, 'scheduleId', event.target?.value);
                                }}
                                fieldSelect="scheduleId"
                                currentState={state.scheduleId}
                                labelField="schedule"
                                showWarning={scheduleInactive.value}
                                customLabelId="schedule-id"
                                customId="schedule-id"
                                error={errors.scheduleId}
                                customClassName={`${
                                    scheduleInactive.value ? 'form-action-type inactive' : 'form-action-type'
                                }`}
                            />
                        </FormControl>
                    </div>
                )}
            </div>
        );
    },
);

export default Step2HeatMap;
